import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

/** 高德街道瓦片（中文路名，zoom 18 可到街道/小区级） */
const GAODE_STREET = 'https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}'
const ESRI_SATELLITE = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
const GAODE_SUBDOMAINS = ['1', '2', '3', '4']

export const MAP_BASE_MODES = {
  street: 'street',
  satellite: 'satellite'
}

/** 地图选点默认：上海市中心（人民广场附近） */
export const SHANGHAI_MAP_CENTER = [31.230416, 121.473701]
export const SHANGHAI_MAP_ZOOM = 11

/** 全国概览：中心偏华中，zoom 5 全国视野（进入页默认） */
export const CHINA_MAP_VIEW = {
  center: [36.0, 104.5],
  zoom: 5
}

const STATUS_COLOR = {
  online: '#52c41a',
  offline: '#bfbfbf',
  alarm: '#ff4d4f'
}

export function statusColor(status) {
  return STATUS_COLOR[status] || STATUS_COLOR.offline
}

export function createDeviceMap(container, options = {}) {
  const map = L.map(container, {
    center: options.center || CHINA_MAP_VIEW.center,
    zoom: options.zoom != null ? options.zoom : CHINA_MAP_VIEW.zoom,
    minZoom: 3,
    maxZoom: 18,
    zoomControl: false
  })
  L.control.zoom({ position: 'topright' }).addTo(map)
  L.control.scale({ imperial: false, position: 'bottomleft' }).addTo(map)
  setMapBaseLayer(map, options.baseMode || MAP_BASE_MODES.street)
  return map
}

export function addGaodeTileLayer(map) {
  return addStreetLayer(map)
}

export function setMapBaseLayer(map, mode = MAP_BASE_MODES.street) {
  if (!map) return []
  const oldLayers = map._gasBaseLayers || []
  oldLayers.forEach(layer => {
    if (map.hasLayer(layer)) map.removeLayer(layer)
  })
  const nextMode = mode === MAP_BASE_MODES.satellite ? MAP_BASE_MODES.satellite : MAP_BASE_MODES.street
  const nextLayers = nextMode === MAP_BASE_MODES.satellite ? addSatelliteLayers(map) : [addStreetLayer(map)]
  const refresh = () => {
    map.invalidateSize()
    nextLayers.forEach(layer => {
      if (layer && typeof layer.redraw === 'function') layer.redraw()
    })
  }
  map._gasBaseMode = nextMode
  map._gasBaseLayers = nextLayers
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(refresh)
  } else {
    refresh()
  }
  return nextLayers
}

function addStreetLayer(map) {
  return L.tileLayer(GAODE_STREET, {
    subdomains: GAODE_SUBDOMAINS,
    maxZoom: 18,
    attribution: '&copy; 高德地图'
  }).addTo(map)
}

function addSatelliteLayers(map) {
  const image = L.tileLayer(ESRI_SATELLITE, {
    maxZoom: 19,
    attribution: 'Tiles &copy; Esri'
  }).addTo(map)
  return [image]
}

/** 设备表单地图选点（Leaflet + 高德瓦片，不依赖 JS API 渲染底图） */
export function createCoordPickerMap(container, options = {}) {
  const map = L.map(container, {
    center: options.center || SHANGHAI_MAP_CENTER,
    zoom: options.zoom != null ? options.zoom : SHANGHAI_MAP_ZOOM,
    minZoom: 3,
    maxZoom: 18,
    zoomControl: true
  })
  addGaodeTileLayer(map)
  return map
}

export function buildPickerMarkerIcon() {
  return L.divIcon({
    className: 'coord-picker-marker-wrap',
    html: '<span class="coord-picker-marker"></span>',
    iconSize: [22, 22],
    iconAnchor: [11, 11]
  })
}

export function buildMarkerIcon(status, precise) {
  const color = statusColor(status)
  const border = precise ? '2px solid #fff' : '2px dashed #fff'
  return L.divIcon({
    className: 'device-map-marker-wrap',
    html: `<span class="device-map-marker ${status}${precise ? '' : ' approx'}" style="background:${color};border:${border}"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7]
  })
}

export function buildDevicePopup(device, statusLabel) {
  const name = device.deviceName || '未命名设备'
  const code = device.deviceCode || '—'
  const site = device.siteName || '—'
  const st = statusLabel(device.mapStatus)
  const type = device.deviceType || device.terminalType || ''
  const subs = Array.isArray(device._popupSubDevices) ? device._popupSubDevices.filter(s => s && s.code) : []
  const showSubs = subs.length > 1
  const metricRows = showSubs
    ? subs.map((sub, index) => `<div class="map-popup-sub-panel"${index === 0 ? '' : ' hidden'} data-sub="${escapeHtml(sub.code)}">${buildMetricRowsHtml(sub.metrics)}</div>`).join('')
    : buildMetricRowsHtml(device._popupMetrics)
  const tabs = showSubs
    ? `<div class="map-popup-tabs">${subs.map((sub, index) => `<button type="button" class="map-popup-tab${index === 0 ? ' active' : ''}" data-sub="${escapeHtml(sub.code)}">${escapeHtml(sub.label || sub.code)}</button>`).join('')}</div>`
    : ''
  const precise = device._preciseCoord
  const locHint = precise ? '' : '<div class="map-popup-hint">未填经纬度，位置为区域估算</div>'
  let alarm = ''
  if (device.mapStatus === 'alarm' && device.alarmMessage) {
    alarm = `<div class="map-popup-alarm">${escapeHtml(device.alarmMessage)}</div>`
  }
  return `<div class="map-popup">
    <div class="map-popup-title">${escapeHtml(name)}</div>
    <div class="map-popup-code">${escapeHtml(code)}</div>
    <div class="map-popup-row">状态：${escapeHtml(st)}</div>
    <div class="map-popup-row">&#31867;&#22411;&#65306;${escapeHtml(type || '-')}</div>
    <div class="map-popup-site">${escapeHtml(site)}</div>
    ${tabs}
    ${metricRows}
    ${locHint}${alarm}
  </div>`
}

function buildMetricRowsHtml(metrics) {
  const list = Array.isArray(metrics) ? metrics : []
  if (!list.length) return ''
  return `<div class="map-popup-metrics">${list.map(item => {
    const unit = item.unit ? `<small>${escapeHtml(item.unit)}</small>` : ''
    const value = item.value == null || item.value === '' ? '-' : item.value
    return `<div class="map-popup-metric"><span>${escapeHtml(item.label || item.key || '')}</span><strong>${escapeHtml(value)}${unit}</strong></div>`
  }).join('')}</div>`
}

function bindPopupSubDeviceTabs(marker) {
  const popup = marker.getPopup()
  const root = popup && popup.getElement()
  if (!root) return
  const tabs = root.querySelectorAll('.map-popup-tab')
  const panels = root.querySelectorAll('.map-popup-sub-panel')
  if (!tabs.length || !panels.length) return
  tabs.forEach(tab => {
    tab.addEventListener('click', event => {
      event.preventDefault()
      event.stopPropagation()
      const code = tab.getAttribute('data-sub')
      tabs.forEach(item => item.classList.toggle('active', item === tab))
      panels.forEach(panel => {
        if (panel.getAttribute('data-sub') === code) panel.removeAttribute('hidden')
        else panel.setAttribute('hidden', '')
      })
    })
  })
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** @returns {{ layer: L.LayerGroup, markers: L.Marker[], byId: Map<number,L.Marker> }} */
export function renderDeviceMarkers(map, devices, { coordResolver, statusLabel, onMarkerClick, onMarkerHover }) {
  const layer = L.layerGroup().addTo(map)
  const markers = []
  const byId = new Map()

  devices.forEach(device => {
    const [lng, lat] = coordResolver(device)
    const precise = device._preciseCoord === true
    const marker = L.marker([lat, lng], {
      icon: buildMarkerIcon(device.mapStatus, precise),
      title: device.deviceName || device.deviceCode || ''
    })
    marker.bindPopup(buildDevicePopup(device, statusLabel), {
      maxWidth: 380,
      closeButton: true,
      autoPan: true
    })
    marker.on('popupopen', () => bindPopupSubDeviceTabs(marker))
    marker.on('mouseover', () => {
      marker.openPopup()
      if (typeof onMarkerHover === 'function') onMarkerHover(device)
    })
    marker.on('click', () => {
      if (typeof onMarkerClick === 'function') onMarkerClick(device)
    })
    marker.addTo(layer)
    markers.push(marker)
    if (device.deviceId != null) byId.set(device.deviceId, marker)
  })

  return { layer, markers, byId }
}

/** 进入页面默认：全国视野，设备点仍显示在对应经纬度 */
export function setMapToChinaView(map) {
  if (!map) return
  map.invalidateSize()
  map.setView(CHINA_MAP_VIEW.center, CHINA_MAP_VIEW.zoom, { animate: false })
}

export function fitMapToMarkers(map, markers, { maxZoom = 18, zoomOffset = 0, padding = 0.12 } = {}) {
  if (!map) return
  map.invalidateSize()
  if (!markers.length) {
    setMapToChinaView(map)
    return
  }
  const group = L.featureGroup(markers)
  map.fitBounds(group.getBounds().pad(padding), { maxZoom })
  if (zoomOffset) {
    const next = Math.min(map.getZoom() + zoomOffset, map.getMaxZoom())
    map.setZoom(next)
  }
}

export function focusMarker(map, marker, zoom = 18) {
  if (!map || !marker) return
  const latLng = marker.getLatLng()
  map.setView(latLng, zoom, { animate: true })
  marker.openPopup()
}
